import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// feat_296 — administración de plantillas del checklist. Una plantilla es un
// molde: al crear una tarea con `templateId` sus items se copian vacíos.
// Editar una plantilla NO afecta a las tareas ya instanciadas.

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  row: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  detail: { marginTop: theme.spacing(2), padding: theme.spacing(2) },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  grow: { flexGrow: 1 },
}));

const TemplatesManager = () => {
  const classes = useStyles();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [newName, setNewName] = useState("");
  const [newClientType, setNewClientType] = useState("");

  const [itemType, setItemType] = useState("check");
  const [itemLabel, setItemLabel] = useState("");
  const [itemFieldKind, setItemFieldKind] = useState("text");
  const [itemOptions, setItemOptions] = useState("");

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/checklist/templates");
      setTemplates(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const selected = templates.find((t) => t.id === selectedId) || null;

  // Reemplaza una plantilla en la lista con la versión fresca del backend
  // (todos los endpoints de plantilla devuelven la plantilla completa).
  const replaceTemplate = (fresh) =>
    setTemplates((prev) => prev.map((t) => (t.id === fresh.id ? fresh : t)));

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/checklist/templates", {
        name,
        clientType: newClientType.trim() || null,
      });
      setTemplates((prev) => [...prev, data]);
      setSelectedId(data.id);
      setNewName("");
      setNewClientType("");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("¿Eliminar la plantilla? Las tareas ya creadas no se tocan.")) return;
    setBusy(true);
    try {
      await api.delete(`/superadmin/checklist/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleAddItem = async () => {
    const label = itemLabel.trim();
    if (!label || !selected) return;
    const payload = { type: itemType, label };
    if (itemType === "field") {
      payload.fieldKind = itemFieldKind;
      if (itemFieldKind === "select") {
        payload.options = itemOptions
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean);
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/superadmin/checklist/templates/${selected.id}/items`,
        payload
      );
      replaceTemplate(data);
      setItemLabel("");
      setItemOptions("");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/superadmin/checklist/template-items/${itemId}`
      );
      replaceTemplate(data);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleMoveItem = async (index, delta) => {
    if (!selected) return;
    const items = selected.items || [];
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    setBusy(true);
    try {
      const { data } = await api.put(
        `/superadmin/checklist/templates/${selected.id}/items/reorder`,
        { itemIds: ids }
      );
      replaceTemplate(data);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="h6" gutterBottom>
        Plantillas
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Una plantilla es un molde de items. Al crear una tarea con plantilla, sus
        items se copian vacíos; editarla después no afecta a las tareas ya creadas.
      </Typography>

      <div className={classes.row}>
        <TextField
          size="small"
          label="Nombre de la plantilla"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <TextField
          size="small"
          label="Tipo de cliente (opcional)"
          value={newClientType}
          onChange={(e) => setNewClientType(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled={busy || !newName.trim()}
          onClick={handleCreate}
        >
          Crear plantilla
        </Button>
      </div>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Tipo de cliente</TableCell>
              <TableCell align="center">Items</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No hay plantillas todavía.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map((t) => (
                <TableRow
                  key={t.id}
                  hover
                  selected={t.id === selectedId}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedId(t.id === selectedId ? null : t.id)}
                >
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    {t.clientType ? <Chip size="small" label={t.clientType} /> : "—"}
                  </TableCell>
                  <TableCell align="center">{(t.items || []).length}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(t.id);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {selected && (
        <Paper variant="outlined" className={classes.detail}>
          <Typography variant="subtitle1">Items de "{selected.name}"</Typography>
          <Divider style={{ margin: "8px 0" }} />

          {(selected.items || []).length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Sin items. Agregá el primero abajo.
            </Typography>
          ) : (
            (selected.items || []).map((item, index) => (
              <div key={item.id} className={classes.itemRow}>
                <Chip
                  size="small"
                  label={
                    item.type === "check"
                      ? "Check"
                      : item.fieldKind === "select"
                      ? "Lista"
                      : "Texto"
                  }
                />
                <Typography variant="body2" className={classes.grow}>
                  {item.label}
                  {item.fieldKind === "select" && item.options && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ marginLeft: 8 }}
                    >
                      ({item.options.join(" · ")})
                    </Typography>
                  )}
                </Typography>
                <IconButton
                  size="small"
                  disabled={busy || index === 0}
                  onClick={() => handleMoveItem(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={busy || index === (selected.items || []).length - 1}
                  onClick={() => handleMoveItem(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={() => handleDeleteItem(item.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            ))
          )}

          <Divider style={{ margin: "12px 0" }} />

          <div className={classes.row} style={{ marginBottom: 0 }}>
            <Select
              value={itemType}
              onChange={(e) => setItemType(e.target.value)}
              style={{ minWidth: 110 }}
            >
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="field">Campo</MenuItem>
            </Select>
            {itemType === "field" && (
              <Select
                value={itemFieldKind}
                onChange={(e) => setItemFieldKind(e.target.value)}
                style={{ minWidth: 110 }}
              >
                <MenuItem value="text">Texto</MenuItem>
                <MenuItem value="select">Lista</MenuItem>
              </Select>
            )}
            <TextField
              size="small"
              label="Etiqueta"
              value={itemLabel}
              onChange={(e) => setItemLabel(e.target.value)}
              style={{ minWidth: 220 }}
            />
            {itemType === "field" && itemFieldKind === "select" && (
              <TextField
                size="small"
                label="Opciones (separadas por coma)"
                value={itemOptions}
                onChange={(e) => setItemOptions(e.target.value)}
                style={{ minWidth: 260 }}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={busy || !itemLabel.trim()}
              onClick={handleAddItem}
            >
              Agregar item
            </Button>
          </div>
        </Paper>
      )}
    </Paper>
  );
};

export default TemplatesManager;
